import { Column } from '@ant-design/plots';
import { Col, DatePicker, Radio, Row, Select, Spin, Switch, Tooltip, Typography } from 'antd';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';

import useDebounce from '~/hooks/useDebounce';
import api from '~/services/api';

import { Container, FiltersCard, SummaryCard, SummaryGrid } from './styles';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const MONTH_ORDER = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const RETURN_REASONS = [
  'Claim damage',
  'Defective',
  'Delivery issue',
  'Different/Wrong Item',
  'No longer needed',
  'No reason given',
  'Overstock',
  'Product dissatisfaction',
  'Repair service',
  'Wrong description',
];
const RETURN_REASON_DESCRIPTIONS = {
  'Claim damage': 'A physical damage issue occurred during transportation.',
  'Delivery issue': 'The package did not successfully reach the customer, or was late.',
  'Different/Wrong Item': 'The item the customer received was wrong.',
  'Repair service': 'The customer is paying for us to repair their unit.',
  'No reason given': 'No reason was given or specified as to why the return occurred.',
  Overstock: 'The unit was shipped to a distribution facility for holding and it was returned back to us.',
  'Product dissatisfaction': 'The customer was dissatisfied with the quality of item they received.',
  Defective: 'A defect occurred and the item does not function as intended.',
  'Wrong description': 'The item received was different than what was advertised.',
  'No longer needed': 'The customer does not need or want the item anymore.',
};
const RETURN_REASON_OPTIONS = RETURN_REASONS.map((reason) => ({
  value: reason,
  label: (
    <Tooltip title={RETURN_REASON_DESCRIPTIONS[reason]} placement="right">
      <span>{reason}</span>
    </Tooltip>
  ),
}));
const DEFAULT_SELECTED_RETURN_REASONS = ['Claim damage', 'Different/Wrong Item', 'Defective', 'Wrong description'];
const DEFAULT_EXCLUDE_WORDS = [];
const THRESHOLD_PERCENT = 5;
const DEBOUNCE_MS = 500;

function buildInitialFilters() {
  return {
    dateRange: [moment().subtract(2, 'year').startOf('year'), moment()],
    storeIds: [],
    returnReasons: [...DEFAULT_SELECTED_RETURN_REASONS],
    excludeActionWords: [...DEFAULT_EXCLUDE_WORDS],
    includeReplacementOrders: true,
    includeManualFBAOrders: true,
    includeZeroTotalOrders: false,
  };
}

function buildQueryParams(filters) {
  const { dateRange, storeIds, returnReasons, excludeActionWords, includeReplacementOrders, includeManualFBAOrders, includeZeroTotalOrders } = filters;
  return {
    from: dateRange[0].format('YYYY-MM-DD'),
    to: dateRange[1].format('YYYY-MM-DD'),
    includeReplacementOrders,
    includeManualFBAOrders,
    includeZeroTotalOrders,
    ...(storeIds.length && { storeIds: storeIds.join(',') }),
    ...(returnReasons.length && { returnReasons: returnReasons.join(',') }),
    ...(excludeActionWords.length && { excludeActionWords: excludeActionWords.join(',') }),
  };
}

function sortedByMonth(data) {
  return [...data].sort((a, b) => MONTH_ORDER.indexOf(a.month) - MONTH_ORDER.indexOf(b.month));
}

function sortedChronologically(data) {
  return [...data]
    .sort((a, b) => {
      if (a.year !== b.year) return Number(a.year) - Number(b.year);
      return MONTH_ORDER.indexOf(a.month) - MONTH_ORDER.indexOf(b.month);
    })
    .map((d) => ({ ...d, monthYear: `${d.month} ${d.year}` }));
}

function computeYearlyStats(data) {
  return data.reduce((acc, entry) => {
    if (!acc[entry.year]) {
      acc[entry.year] = { totalPercent: 0, monthsWithData: 0, shipped: 0, returned: 0 };
    }
    if (entry.percent > 0) {
      acc[entry.year].totalPercent += entry.percent;
      acc[entry.year].monthsWithData += 1;
    }
    acc[entry.year].shipped += entry.shipped;
    acc[entry.year].returned += entry.returned;
    return acc;
  }, {});
}

function buildTooltipContent(title, items) {
  if (!items?.length) return '';
  const rows = items
    .map(
      (item) => `
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:20px;padding:3px 0">
          <span style="display:flex;align-items:center;gap:6px;white-space:nowrap">
            <span style="width:8px;height:8px;border-radius:50%;background:${item.color};display:inline-block;flex-shrink:0"></span>
            ${item.name}
          </span>
          <div style="text-align:right">
            <span style="font-weight:700;font-size:13px">${item.value}%</span>
            <div style="color:#8c8c8c;font-size:11px;margin-top:1px">
              ${item.data.returned} returned / ${item.data.shipped} shipped
            </div>
          </div>
        </div>`
    )
    .join('');
  return `
    <div style="padding:10px 14px;min-width:230px">
      <div style="font-weight:600;font-size:13px;border-bottom:1px solid #f0f0f0;padding-bottom:6px;margin-bottom:6px">${title}</div>
      ${rows}
    </div>`;
}

const THRESHOLD_ANNOTATIONS = [
  {
    type: 'line',
    start: ['min', THRESHOLD_PERCENT],
    end: ['max', THRESHOLD_PERCENT],
    style: { stroke: '#F4664A', lineDash: [4, 4], lineWidth: 2 },
  },
  {
    type: 'text',
    position: ['min', THRESHOLD_PERCENT],
    content: `   ${THRESHOLD_PERCENT}% threshold`,
    offsetY: -10,
    style: { fill: '#F4664A', fontSize: 10 },
  },
];

export function RMA() {
  const [filters, setFilters] = useState(buildInitialFilters);
  const [data, setData] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chartMode, setChartMode] = useState('grouped');

  const debouncedFilters = useDebounce(filters, DEBOUNCE_MS);

  useEffect(() => {
    async function loadStores() {
      try {
        const { data: storesData } = await api.get('stores');
        setStores(storesData.map((s) => ({ label: s.name, value: s.id })));
      } catch {
        setStores([]);
        // non-critical: store list simply won't be populated
      }
    }
    loadStores();
  }, []);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const res = await api.get('kpi/rmaYearly', { params: buildQueryParams(debouncedFilters) });
        setData(res.data);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [debouncedFilters]); // eslint-disable-line

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  const groupedData = sortedByMonth(data);
  const sequentialData = sortedChronologically(data);
  const yearlyStats = computeYearlyStats(data);

  const sharedChartConfig = {
    yField: 'percent',
    seriesField: 'year',
    isGroup: true,
    yAxis: { title: { text: 'Return Rate (%)' }, min: 0 },
    legend: { position: 'top-left' },
    annotations: THRESHOLD_ANNOTATIONS,
    tooltip: { customContent: buildTooltipContent },
  };

  const groupedChartConfig = {
    ...sharedChartConfig,
    data: groupedData,
    xField: 'month',
    label: {
      content: (item) => (item.percent > 0 ? `${item.percent}%` : ''),
      position: 'top',
      style: { fontSize: 10, fill: '#555' },
    },
  };

  const sequentialChartConfig = {
    ...sharedChartConfig,
    data: sequentialData,
    xField: 'monthYear',
    label: null,
    xAxis: { label: { autoRotate: true } },
  };

  const activeChartConfig = chartMode === 'grouped' ? groupedChartConfig : sequentialChartConfig;

  return (
    <Container>
      <Title level={3}>Return Merchandise Authorization (RMA) Rate</Title>

      <FiltersCard title="Filters" size="small">
        <Row gutter={[16, 14]}>
          <Col xs={24} md={12} xl={8}>
            <Text strong>Date Range</Text>
            <RangePicker
              style={{ width: '100%', marginTop: 4 }}
              value={filters.dateRange}
              format="YYYY-MM-DD"
              allowClear={false}
              onChange={(dates) => dates && updateFilter('dateRange', dates)}
            />
          </Col>

          <Col xs={24} md={12} xl={8}>
            <Text strong>Stores</Text>
            <Select
              mode="multiple"
              style={{ width: '100%', marginTop: 4 }}
              placeholder="All stores"
              options={stores}
              value={filters.storeIds}
              onChange={(val) => updateFilter('storeIds', val)}
              allowClear
              showSearch
              optionFilterProp="label"
            />
          </Col>

          <Col xs={24} md={12} xl={8}>
            <Text strong>Return Reasons</Text>
            <Select
              mode="tags"
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Add return reasons"
              value={filters.returnReasons}
              onChange={(val) => updateFilter('returnReasons', val)}
              options={RETURN_REASON_OPTIONS}
            />
          </Col>

          <Col xs={24} md={12} xl={8}>
            <Text strong>Exclude Action Words</Text>
            <Text type="secondary" style={{ display: 'block', fontSize: 11, marginBottom: 4 }}>
              Type any word and press <kbd>Enter</kbd> to add it — orders containing these words in their action will be excluded.
            </Text>
            <Select
              mode="tags"
              style={{ width: '100%' }}
              placeholder="Type a word and press Enter"
              value={filters.excludeActionWords}
              onChange={(val) => updateFilter('excludeActionWords', val)}
            />
          </Col>

          <Col xs={24} xl={16}>
            <Text strong>Options</Text>
            <div className="options-row">
              <label className="switch-label">
                <Switch checked={filters.includeReplacementOrders} onChange={(val) => updateFilter('includeReplacementOrders', val)} />
                <span>Include Replacement Orders (RO)</span>
              </label>
              <label className="switch-label">
                <Switch checked={filters.includeManualFBAOrders} onChange={(val) => updateFilter('includeManualFBAOrders', val)} />
                <span>Include Manual Orders sent to FBA WH (FBA)</span>
              </label>
              <label className="switch-label">
                <Switch checked={filters.includeZeroTotalOrders} onChange={(val) => updateFilter('includeZeroTotalOrders', val)} />
                <span>Include Zero-total Orders</span>
              </label>
            </div>
          </Col>
        </Row>
      </FiltersCard>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Radio.Group value={chartMode} onChange={(e) => setChartMode(e.target.value)} buttonStyle="solid">
          <Radio.Button value="grouped">Compare by Month</Radio.Button>
          <Radio.Button value="sequential">Timeline</Radio.Button>
        </Radio.Group>
      </div>

      <Spin spinning={loading}>
        <div style={{ minHeight: 380 }}>
          <Column {...activeChartConfig} style={{ height: 380 }} />
        </div>
      </Spin>

      {Object.keys(yearlyStats).length > 0 && (
        <>
          <Title level={5} style={{ marginTop: 28 }}>
            Yearly Summary
          </Title>
          <SummaryGrid>
            {Object.entries(yearlyStats)
              .sort(([a], [b]) => Number(a) - Number(b))
              .map(([year, stats]) => {
                const avg = stats.monthsWithData > 0 ? (stats.totalPercent / stats.monthsWithData).toFixed(2) : '0.00';
                return (
                  <SummaryCard key={year}>
                    <span className="year">{year}</span>
                    <span className="avg">{avg}%</span>
                    <span className="avg-label">avg return rate</span>
                    <div className="counts">
                      <span>
                        <b>{stats.shipped.toLocaleString()}</b> shipped
                      </span>
                      <span>
                        <b>{stats.returned.toLocaleString()}</b> returned
                      </span>
                    </div>
                  </SummaryCard>
                );
              })}
          </SummaryGrid>
        </>
      )}
    </Container>
  );
}
